import { cx } from 'class-variance-authority';
import { useRouter } from 'next/router';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import ChatWidgetFullscreenSlot from '@/components/agent-chat/components/chat-widget/chat-widget-fullscreen-slot';
import ChatWidgetSlot from '@/components/agent-chat/components/chat-widget/chat-widget-slot';
import useChatWidgetSlotWidth from '@/components/agent-chat/hooks/use-chat-widget-slot-width';
import useForceNavCollapsed from '@/components/layout/hooks/use-force-nav-collapsed';
import useSideNavCollapsed from '@/components/layout/hooks/use-side-nav-collapsed';
import { MAIN_PAGE_ID } from '@/config/constants';
import useMobileViewport from '@/hooks/use-mobile-viewport';
import useAssumeBanner from '../assume-banner/hooks/use-assume-banner';
import { Banners } from '../default-layout/banners';
import ResponsiveNav from '../responsive-nav';
const COLLAPSED_PATH_PREFIXES = ['/investigations/', '/chat/', '/playbooks/', '/agents/', '/users/', '/businesses/'];
type FullLayoutProps = {
  children: React.ReactNode;
};
const FullLayout = ({
  children
}: FullLayoutProps) => {
  const {
    isCollapsed: storedCollapsed,
    setCollapsed,
    setCollapsedDefault
  } = useSideNavCollapsed();
  const isFirstRender = useRef(true);
  const mobileViewport = useMobileViewport();
  const {
    rowRef,
    writeWidthVar,
    setResizing
  } = useChatWidgetSlotWidth();
  const {
    pathname
  } = useRouter();
  const forced = useForceNavCollapsed();

  // The builders + investigation details always start collapsed for the room, ignoring the
  // saved preference — but a manual expand may still win for this visit. Kept out of the
  // persisted store so it can't leak to other routes; reset on route change so re-entering
  // re-collapses.
  const [visitExpanded, setVisitExpanded] = useState(false);
  useEffect(() => {
    setVisitExpanded(false);
  }, [pathname]);

  // Soft default for the non-forced full-layout routes (/users, /businesses, /chat): collapse
  // on first visit, but defer to the user's choice once made. Forced routes own their collapse
  // via visitExpanded, so skip the default there.
  useEffect(() => {
    isFirstRender.current = false;
    if (!forced && shouldStartCollapsed()) setCollapsedDefault(true);
  }, [forced, setCollapsedDefault]);
  const {
    isAssumedSessionEditMode
  } = useAssumeBanner();
  const isCollapsed = forced ? !visitExpanded : storedCollapsed;
  // On a forced route the toggle only flips this visit (non-persisted); elsewhere it records
  // the user's preference in the shared store.
  const handleNavCollapse = (next: boolean) => {
    if (forced) setVisitExpanded(!next);else setCollapsed(next);
  };

  // Snap the nav column (no grid animation) when its width flips because the ROUTE changed —
  // e.g. builder (collapsed) → /ask-percy (expanded). Fullscreen Percy fills the grid's 1fr
  // column, so animating the nav there drags the chat's width mid-transition. An in-place
  // toggle (same route) still animates. Ref updates post-paint, like use-chat-widget-slot-width.
  const paintedRef = useRef({
    pathname,
    isCollapsed
  });
  const snapNav = paintedRef.current.pathname !== pathname && paintedRef.current.isCollapsed !== isCollapsed;
  useEffect(() => {
    paintedRef.current = {
      pathname,
      isCollapsed
    };
  });
  return <div data-testid="private-full-layout" data-keyboard-open={mobileViewport?.keyboardOpen || undefined} style={mobileViewport && {
    height: mobileViewport.height,
    top: mobileViewport.top
  }} className={cx('group/app grid grid-cols-1 grid-rows-[auto_minmax(0,1fr)] md:grid-rows-1 md:grid-cols-[var(--side-nav-width)_1fr] h-dvh pt-[env(safe-area-inset-top)] md:pt-0 overflow-hidden relative max-md:fixed max-md:inset-x-0 max-md:top-0 bg-canvas min-h-0', !isFirstRender.current && !snapNav && 'transition-[grid-template-columns] duration-200', {
    'md:grid-cols-[var(--side-nav-width-collapsed)_1fr]': isCollapsed
  })}>
      <ResponsiveNav isCollapsed={isCollapsed} onSideNavCollapse={handleNavCollapse} />
      {/* Flex row: content card + the docked chat as a sibling column. The row
          owns --chat-widget-width (survives the slot unmounting); data-resizing
          kills the card transition mid-drag. */}
      <div ref={rowRef} className="col-start-1 row-start-2 md:col-start-auto md:row-start-auto group/chat flex min-w-0 min-h-full overflow-hidden md:relative">
        {/* Card width tracks the slot width var + transitions, so the page reflow
            animates as the sidebar docks in/out. Inset keeps the card rounded. */}
        <div className={cx('flex flex-col min-w-0 overflow-hidden md:py-2 md:pr-2 w-[calc(100%-var(--chat-widget-width,0px))]',
      // Reflow animates only on a real dock/undock (armed by data-animate-width on
      // the row — see use-chat-widget-slot-width), never on layout mount.
      'group-data-animate-width/chat:transition-[width] group-data-animate-width/chat:duration-200', 'group-data-resizing/chat:transition-none')}>
          <div className={cx('md:relative bg-primary md:rounded-lg overflow-hidden flex flex-col flex-1 w-full', {
          'border-error border-[1.5px]': isAssumedSessionEditMode,
          'md:border md:border-tertiary': !isAssumedSessionEditMode
        })}>
            <Banners />
            <main id={MAIN_PAGE_ID} className="relative flex-1 min-w-0 overflow-hidden">
              {children}
            </main>
            {/* Fullscreen Percy portals here: absolute-fills the card, overlaying the page content. On a
                phone neither the card nor the row is positioned, so it fills the app shell instead. */}
            <ChatWidgetFullscreenSlot />
          </div>
        </div>
        {/* Docked Percy chat: full-height column beside the card, flush right. */}
        <ChatWidgetSlot onResizeWidth={writeWidthVar} onResizingChange={setResizing} />
      </div>
    </div>;
};
const shouldStartCollapsed = (): boolean => {
  if (typeof window === 'undefined') return false;
  const path = window.location.pathname;
  return COLLAPSED_PATH_PREFIXES.some(prefix => path.startsWith(prefix));
};
export default FullLayout;